import {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";

import {
  ClassicEditor,
  AutoImage,
  Autosave,
  BlockQuote,
  Bold,
  CloudServices,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Paragraph,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TodoList,
  Underline,
  WordCount,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

const LICENSE_KEY = "GPL";

const Editor = forwardRef(
  ({ setEmbeddedImgs, onChange, topBar, initialData }, ref) => {
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null); // ⭐ store actual CKEditor instance
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  // ======================================================
  // ⭐ Upload Adapter
  // ======================================================
  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise(async (resolve) => {
          try {
            const file = await loader.file;
            const uploadedFileName = file.name;
            const cidUrl = `cid:${uploadedFileName}`;

            setEmbeddedImgs((prev) => [
              ...prev,
              { id: loader.id, cidUrl, file },
            ]);

            // CKEditor expects an object with default upload structure
            resolve({
              default: cidUrl,
            });
          } catch (error) {
            console.error("Upload error:", error);
          }
        }),
      abort: () => {},
    };
  }

  function uploadPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return uploadAdapter(loader);
    };
  }

  // ======================================================
  // ⭐ Handle parent method exposure (setTemplate/getContent)
  // ======================================================
  useImperativeHandle(ref, () => ({
    setTemplate(html) {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.setData(html);
      }
    },

    getContent() {
      return editorInstanceRef.current?.getData() || "";
    },

    insertText(text) {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.model.change(writer => {
          editorInstanceRef.current.model.insertContent(writer.createText(text));
        });
      }
    },
  }));

  // ======================================================
  // ⭐ Setup layout
  // ======================================================
  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  useEffect(() => {
    if (
      editorInstanceRef.current &&
      initialData !== undefined &&
      initialData !== null &&
      initialData !== ""
    ) {
      const currentData = editorInstanceRef.current.getData();
      if (!currentData || currentData === "<p></p>" || currentData === "<p>&nbsp;</p>") {
        editorInstanceRef.current.setData(initialData);
      }
    }
  }, [initialData]);

  // ======================================================
  // ⭐ Editor Config
  // ======================================================
  const { editorConfig } = useMemo(() => {
    if (!isLayoutReady) return {};

    return {
      editorConfig: {
        placeholder: "Start writing your email...",
        toolbar: {
          items: [
            "heading",
            "|",
            "bold",
            "italic",
            "underline",
            "|",
            "link",
            "imageUpload",
            "insertTable",
            "blockQuote",
            "|",
            "bulletedList",
            "numberedList",
            "todoList",
            "outdent",
            "indent",
          ],
          shouldNotGroupWhenFull: false,
        },

        plugins: [
          AutoImage,
          Autosave,
          BlockQuote,
          Bold,
          CloudServices,
          Essentials,
          Heading,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          Paragraph,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TodoList,
          Underline,
          WordCount,
        ],

        heading: {
          options: [
            {
              model: "paragraph",
              title: "Paragraph",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h1",
              title: "Heading 1",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h2",
              title: "Heading 2",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h3",
              title: "Heading 3",
              class: "ck-heading_heading3",
            },
            {
              model: "heading4",
              view: "h4",
              title: "Heading 4",
              class: "ck-heading_heading4",
            },
            {
              model: "heading5",
              view: "h5",
              title: "Heading 5",
              class: "ck-heading_heading5",
            },
            {
              model: "heading6",
              view: "h6",
              title: "Heading 6",
              class: "ck-heading_heading6",
            },
          ],
        },

        image: {
          toolbar: [
            "toggleImageCaption",
            "imageTextAlternative",
            "|",
            "imageStyle:inline",
            "imageStyle:wrapText",
            "imageStyle:breakText",
            "|",
            "resizeImage",
          ],
        },

        initialData: "",
        licenseKey: LICENSE_KEY,

        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: "https://",
          decorators: {
            toggleDownloadable: {
              mode: "manual",
              label: "Downloadable",
              attributes: { download: "file" },
            },
          },
        },

        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true,
          },
        },

        table: {
          contentToolbar: [
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableProperties",
            "tableCellProperties",
          ],
        },

        extraPlugins: [uploadPlugin],
      },
    };
  }, [isLayoutReady]);

  // ======================================================
  // ⭐ Render
  // ======================================================
  return (
    <div className="tw-flex tw-justify-center tw-items-center h-100 tw-w-full">
      <div className="editor-unified-container tw-w-full tw-border tw-border-solid tw-border-slate-200 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-shadow-2xs focus-within:tw-border-[#5D44FF] focus-within:tw-ring-2 focus-within:tw-ring-[#5D44FF]/10 tw-transition-all">
        {topBar}
        <div className="editor-container tw-w-full" ref={editorContainerRef}>
          {editorConfig && (
            <CKEditor
              editor={ClassicEditor}
              config={editorConfig}
              data={initialData || ""}
              onReady={(editor) => {
                editorInstanceRef.current = editor; // ⭐ Capture instance
                if (initialData) {
                  editor.setData(initialData);
                }
              }}
              onChange={(event, editor) => {
                onChange(editor.getData());
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default Editor;
